import React, { useEffect, useState } from "react";
import { AlertTriangle, ShieldOff } from "lucide-react";

/**
 * VPN / Apple Private Relay warning.
 *
 * Best-effort client-side detection — true VPN detection requires server-side
 * IP intelligence (Cloudflare, ipqualityscore, etc.). What we DO catch reliably:
 *   - Apple Private Relay (the user has the iCloud+ feature on and is on Safari).
 *   - Common WebRTC IP leaks that flag a VPN.
 *
 * Even without detection, we always render an *informational* notice — XGate's
 * KYC docs are explicit that VPN/Private Relay blocks verification, so a static
 * heads-up is better than silently letting users fail.
 */
export default function VpnWarning({ compact = false }) {
  const [privateRelaySuspected, setPrivateRelaySuspected] = useState(false);

  useEffect(() => {
    // Apple Private Relay strongly correlates with: iCloud+ subscription headers
    // not visible client-side, BUT we CAN check `navigator.connection`, Safari +
    // a `mask.icloud.com` / `mask-h2.icloud.com` resolver. Cheapest signal:
    // Safari on Apple device + the `connection.effectiveType` returning undefined.
    try {
      const ua = navigator.userAgent;
      const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
      const isApple = /(Mac|iPhone|iPad|iPod)/.test(ua);
      const noConn = !navigator.connection;
      if (isApple && isSafari && noConn) {
        setPrivateRelaySuspected(true);
      }
    } catch (e) { /* ignore */ }
  }, []);

  if (compact) {
    return (
      <div className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900" data-testid="vpn-warning-compact">
        <ShieldOff className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          Turn off any <strong>VPN</strong> or <strong>Apple Private Relay</strong> before verifying — KYC will fail otherwise.
        </span>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 space-y-2" data-testid="vpn-warning">
      <div className="flex items-center gap-2 font-display font-bold text-amber-900">
        <AlertTriangle className="w-4 h-4" /> Turn off VPN & Apple Private Relay before continuing
      </div>
      <p className="text-xs text-amber-900/85 leading-relaxed">
        Our identity verification provider rejects connections that route through a VPN, proxy,
        or <strong>iCloud Private Relay</strong>. If you don't disable them first, KYC will fail and
        you'll need to start over.
      </p>
      <ul className="text-xs text-amber-900/80 space-y-1 pl-4 list-disc">
        <li><strong>VPN apps:</strong> disconnect (NordVPN, ExpressVPN, Mullvad, etc.).</li>
        <li><strong>iPhone / iPad:</strong> Settings → Apple ID → iCloud → Private Relay → Off.</li>
        <li><strong>Mac (Safari):</strong> System Settings → Apple ID → iCloud → Private Relay → Off.</li>
        <li><strong>Browser:</strong> avoid Tor, Brave Tor windows, and incognito with proxy extensions.</li>
      </ul>
      {privateRelaySuspected && (
        <div className="text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2 mt-1" data-testid="vpn-warning-prsuspected">
          ⚠ We think you may be using <strong>iCloud Private Relay</strong> right now. Please disable it before continuing.
        </div>
      )}
    </div>
  );
}
